/**
 * DecorService — CRUD for decor planning items.
 * Source of truth is the GuestGuru API; localStorage is a cache.
 *
 * Convention: DecorItem uses snake_case field names (decor_element,
 * color_scheme, vendor_name, created_at, updated_at) matching the
 * @guestguru/shared schema and the raw DB columns.  No mapping needed.
 */

import { DecorItem, DecorCategory } from '../types/Decor';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, planningPath } from './PlanningApiClient';

// ── In-memory cache (source of truth = API) ──────────────────────────────────
const LS_KEY = 'anojeje_decor_items';
let decorCache: DecorItem[] = [];

function load(): DecorItem[] {
  return decorCache;
}

function save(items: DecorItem[]): void {
  decorCache = items;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  } catch {
    // storage quota or unavailable — in-memory still works
  }
}

function loadFromLocalStorage(): DecorItem[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as DecorItem[]) : [];
  } catch {
    return [];
  }
}

function uuid(): string {
  return `decor-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const DECOR_CHANGE_EVENT = 'anojeje_decor_change';

// ── Public API ────────────────────────────────────────────────────────────────

export function loadDecorItems(): DecorItem[] {
  return load();
}

export async function createDecorItem(data: Omit<DecorItem, 'id' | 'created_at' | 'updated_at' | 'stock'> & { event_ids?: string[]; venue_area?: string; stock?: number }): Promise<DecorItem> {
  const now = new Date().toISOString();

  // The server owns the id, and /decor/:id parses it with Number() — the local
  // `decor-<ts>-<rand>` id from uuid() makes every later PUT and DELETE for
  // that item fail validation with a 400, which the fire-and-forget .catch()
  // then hid. Create server-side first and adopt the row's real id.
  const created = await apiPost<{ id: number | string }>(planningPath('/decor'), { ...data, created_at: now, updated_at: now });

  const item: DecorItem = { ...data, stock: data.stock ?? 0, id: String(created.id), created_at: now, updated_at: now };
  save([...load(), item]);
  window.dispatchEvent(new Event(DECOR_CHANGE_EVENT));
  return item;
}

export async function updateDecorItem(id: string, data: Partial<Omit<DecorItem, 'id' | 'created_at'>>): Promise<DecorItem | null> {
  const items = load();
  const idx = items.findIndex(i => i.id === id);
  if (idx === -1) return null;

  // Persist before touching the cache. Writing the cache first made a rejected
  // PUT indistinguishable from a successful one: the edit stayed on screen
  // until the next refresh silently replaced it with the server's stale row.
  await apiPut(planningPath(`/decor/${id}`), data);

  const updated: DecorItem = { ...items[idx], ...data, updated_at: new Date().toISOString() };
  save(items.map((i, n) => n === idx ? updated : i));
  window.dispatchEvent(new Event(DECOR_CHANGE_EVENT));
  return updated;
}

/** Soft-delete: mark decor item as archived */
export function archiveDecorItem(id: string): Promise<DecorItem | null> {
  return updateDecorItem(id, { archived: true });
}

/** Restore an archived decor item */
export function restoreDecorItem(id: string): Promise<DecorItem | null> {
  return updateDecorItem(id, { archived: false });
}

/**
 * Permanent delete — only allowed for already-archived decor items.
 * Throws on refusal or write failure rather than reporting a discardable
 * `false`, which callers dropped on the floor.
 */
export async function deleteDecorItem(id: string): Promise<void> {
  const items = load();
  const target = items.find(i => i.id === id);
  if (!target) throw new Error('That decor item is no longer listed — refresh and try again.');
  if (!target.archived) throw new Error('Archive this decor item before deleting it permanently.');

  // Delete server-side first; dropping the row from the cache before the
  // DELETE landed made a failed delete look exactly like a successful one.
  await apiDelete(planningPath(`/decor/${id}`));

  save(items.filter(i => i.id !== id));
  window.dispatchEvent(new Event(DECOR_CHANGE_EVENT));
}

/**
 * Adjust a decor item's stock pool by a signed delta — mirrors
 * ProductService's stock pool adjustment. Used by ProcurementOrderEdit's
 * goesTo('decor') delivery branch to top up stock for allocations linked to
 * an existing decor item (see ANS-1).
 */
export async function adjustDecorStock(id: string, delta: number): Promise<DecorItem | null> {
  const items = load();
  const idx = items.findIndex(i => i.id === id);
  const updated = await apiPatch<DecorItem>(planningPath(`/decor/${id}/stock`), { delta });
  if (!updated) return null;
  if (idx !== -1) save(items.map((i, n) => (n === idx ? { ...i, stock: updated.stock } : i)));
  window.dispatchEvent(new Event(DECOR_CHANGE_EVENT));
  return updated;
}

/** One procurement order this decor item's stock was sourced from — powers
 *  the read-only "Sourced from PO-123 (N units)" panel that replaced the
 *  old source_product_id/source_budget_item_id/procurement_order_id
 *  dropdown (see ANS-1). */
export interface DecorAllocationSource {
  allocation_id: string;
  quantity: number;
  notes: string | null;
  order_id: string;
  marketplace_order_id: string | null;
  order_status: string;
  vendor_name: string | null;
}

export async function loadDecorAllocations(id: string): Promise<DecorAllocationSource[]> {
  const res = await apiGet<{ allocations: DecorAllocationSource[] }>(planningPath(`/decor/${id}/allocations`));
  return res?.allocations ?? [];
}

export function getDecorItemsByCategory(category: DecorCategory): DecorItem[] {
  return loadDecorItems().filter(i => i.category === category);
}

export function getDecorStats(): { total: number; confirmed: number; installed: number } {
  const items = loadDecorItems();
  return {
    total: items.length,
    confirmed: items.filter(i => i.status === 'confirmed').length,
    installed: items.filter(i => i.status === 'installed').length,
  };
}

export async function refreshDecorFromApi(): Promise<DecorItem[]> {
  try {
    const raw = await apiGet<unknown>(planningPath('/decor'));
    const rows: Record<string, unknown>[] = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as any)?.items) ? (raw as any).items : [];

    // Rows are already snake_case from the DB — normalise id to string,
    // parse JSONB arrays (Postgres may return them as objects), and
    // derive the convenience imageUrl alias from reference_images[0].
    const items: DecorItem[] = rows.map(r => {
      const refImgs: string[] = Array.isArray(r.reference_images)
        ? (r.reference_images as unknown[]).map(String)
        : [];
      const canvaEmbeds: string[] = Array.isArray(r.canva_embeds)
        ? (r.canva_embeds as unknown[]).map(String)
        : [];
      const eventIds: string[] = Array.isArray(r.event_ids)
        ? (r.event_ids as unknown[]).map(String)
        : [];
      return {
        ...(r as unknown as DecorItem),
        id: String(r.id),
        reference_images: refImgs,
        canva_embeds: canvaEmbeds,
        imageUrl: refImgs.length > 0 ? refImgs[0] : undefined,
        event_ids: eventIds,
        venue_area: typeof r.venue_area === 'string' ? r.venue_area : '',
      };
    });

    save(items);
    window.dispatchEvent(new Event(DECOR_CHANGE_EVENT));
    return items;
  } catch {
    const cached = loadFromLocalStorage();
    if (cached.length > 0) decorCache = cached;
    return cached;
  }
}

export async function syncDecorToApi(): Promise<void> {
  const items = load();
  for (const item of items) {
    try {
      await apiPost(planningPath('/decor'), item);
    } catch {
      // skip individual failures
    }
  }
}
